import { cx } from "@jugl-web/utils";
import { FC, ReactNode, forwardRef } from "react";
import {
  InteractiveContainer,
  InteractiveContainerNativeProps,
} from "../../primitives";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";
import { ReactComponent as VerticalConnectorCompleteIcon } from "./assets/vertical-connector-complete.svg";
import { ReactComponent as VerticalConnectorIcon } from "./assets/vertical-connector.svg";

const LIST_ITEM_BOX_HEIGHT = 48;
const CONNECTOR_CIRCLE_SIZE = 8;

interface RootProps {
  children: ReactNode;
  className?: string;
}

const Root: FC<RootProps> = ({ children, className }) => (
  <div className={cx("flex w-full flex-col gap-2", className)}>{children}</div>
);

interface HeaderProps {
  startColumnLabel: string;
  endColumnLabel: string;
  className?: string;
}

const Header: FC<HeaderProps> = ({
  startColumnLabel,
  endColumnLabel,
  className,
}) => (
  <div className={cx("flex items-center justify-between", className)}>
    <span className="px-2 text-xs text-dark-700">{startColumnLabel}</span>
    <span className="px-2 text-xs text-dark-700">{endColumnLabel}</span>
  </div>
);

interface ListContainerProps {
  orientation: "horizontal" | "vertical";
  children: ReactNode;
  className?: string;
}

const ListContainer: FC<ListContainerProps> = ({
  orientation,
  children,
  className,
}) => {
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      className={cx(
        "flex flex-col",
        isHorizontal ? "gap-2" : "gap-8",
        className
      )}
    >
      {children}
    </div>
  );
};

interface ListItemProps {
  orientation: "horizontal" | "vertical";
  startSlot: ReactNode;
  endSlot: ReactNode;
  isActive: boolean;
  className?: string;
}

const ListItem: FC<ListItemProps> = ({
  orientation,
  startSlot,
  endSlot,
  isActive,
  className,
}) => {
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      className={cx(
        "relative flex items-center",
        isHorizontal ? "flex-row gap-2" : "flex-col gap-1.5",
        className
      )}
    >
      {startSlot}
      {isHorizontal ? (
        <ArrowIcon
          className={cx(
            "shrink-0 transition-colors",
            isActive ? "text-messages-chat-bubble-sender" : "text-grey-400"
          )}
        />
      ) : (
        <div
          className="absolute"
          style={{
            left: -(CONNECTOR_CIRCLE_SIZE / 2),
            top: LIST_ITEM_BOX_HEIGHT / 2 - CONNECTOR_CIRCLE_SIZE / 2,
          }}
        >
          {isActive ? (
            <VerticalConnectorCompleteIcon />
          ) : (
            <VerticalConnectorIcon />
          )}
        </div>
      )}
      {endSlot}
    </div>
  );
};

interface ListItemStaticBoxProps {
  children: ReactNode;
  className?: string;
}

const ListItemStaticBox: FC<ListItemStaticBoxProps> = ({
  children,
  className,
}) => (
  <div
    className={cx(
      "flex w-full items-center gap-2 overflow-hidden rounded-xl bg-grey-100 px-6",
      className
    )}
    style={{ height: LIST_ITEM_BOX_HEIGHT }}
  >
    {children}
  </div>
);

interface ListItemSelectBoxProps extends InteractiveContainerNativeProps {
  children: ReactNode;
  isOpen: boolean;
  onClick?: () => void;
  className?: string;
}

const ListItemSelectBox = forwardRef<HTMLDivElement, ListItemSelectBoxProps>(
  ({ children, isOpen, className, ...props }, ref) => (
    <InteractiveContainer
      ref={ref}
      className={cx(
        "flex w-full items-center justify-between gap-2 overflow-hidden rounded-xl px-6 transition-colors",
        isOpen ? "bg-grey-200" : "bg-grey-100 hover:bg-grey-200",
        className
      )}
      style={{ height: LIST_ITEM_BOX_HEIGHT }}
      {...props}
    >
      <div className="flex items-center gap-2 overflow-hidden">{children}</div>
      <ChevronDownIcon
        className={cx("shrink-0 transition-transform", isOpen && "rotate-180")}
      />
    </InteractiveContainer>
  )
);

interface ListItemBoxLabelProps {
  children: ReactNode;
  className?: string;
}

const ListItemBoxLabel: FC<ListItemBoxLabelProps> = ({
  children,
  className,
}) => (
  <span
    className={cx(
      "truncate font-secondary text-sm font-medium text-dark",
      className
    )}
  >
    {children}
  </span>
);

interface ListItemBoxPlaceholderProps {
  children: ReactNode;
  className?: string;
}

const ListItemBoxPlaceholder: FC<ListItemBoxPlaceholderProps> = ({
  children,
  className,
}) => (
  <span
    className={cx(
      "truncate font-secondary text-sm font-normal text-[#828282]",
      className
    )}
  >
    {children}
  </span>
);

export const MappingList = {
  Root,
  Header,
  ListContainer,
  ListItem,
  ListItemStaticBox,
  ListItemSelectBox,
  ListItemBoxLabel,
  ListItemBoxPlaceholder,
};
